import * as _ from 'lodash';
import * as hash from 'object-hash';
import { useContext, useEffect, useReducer, useRef } from 'react';
import { connect, Dispatch } from 'react-redux';
import { IApplicationState, AppActionCreators, RecipientActionCreators } from '@nkr/store/index';
import { TableWrapper } from '@nkr/views/recipients/donor-pre-select/styles';
import { downloadFile } from '@nkr/services/download-file';
import { Button, Label } from 'react-bootstrap';
import { IAntibodyScreening } from '@nkr/models/index';
import { IAntibodyScreeningResponse, IAntibodyScreeningResultResponse } from '@nkr/store/recipient';
import { toast } from 'react-toastify';
import { RecipientProfileContext } from '../models';
import { antibodyScreeningsReducer, initialState } from './state';
import { ProfileWrapper } from '../styles';
import AntibodyScreeningDetails from './details';

interface IProps {
    saveRef: React.MutableRefObject<any>
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getAntibodyScreenings: (centerId: number, recipientId: number) => Promise<IAntibodyScreeningResponse>,
    getAntibodyScreeningResult: (centerId: number, recipientId: number, screeningId: number) => Promise<IAntibodyScreeningResultResponse>,
    getAntibodyScreeningPdfFile: (centerId: number, recipientId: number, orderId: number) => Promise<any>,
    getAntibodyScreeningRawFile: (centerId: number, recipientId: number, orderId: number) => Promise<any>,
    processAntibodyScreeningOverride: (centerId: number, recipientId: number, screeningResultId: number) => Promise<any>,
}

type TProps =
    IProps
    & IStateProps
    & IDispatchProps;


const AntibodyScreening = (props: TProps) => {
    const context = useContext(RecipientProfileContext);
    const [localState, dispatch] = useReducer(antibodyScreeningsReducer, initialState);
    const autoOpenedRef = useRef(false);

    useEffect(() => {
        getData();
    }, []);

    useEffect(() => {
        if (localState.data == null || localState.data.length === 0 || autoOpenedRef.current) return;
        const params = new URLSearchParams(window.location.search);
        if (params.get('openDetails') !== 'true') return;

        autoOpenedRef.current = true;
        const firstCompleted = localState.data.find((s: IAntibodyScreening) => s.resultsReceivedDate !== '');
        if (firstCompleted) {
            handleViewDetails(firstCompleted.id);
        }
    }, [localState.data]);

    const getData = () => {
        props.showLoading('Getting Antibody Screening Orders, one moment please...').then(() => {
            props.getAntibodyScreenings(context.centerId, context.recipientId)
                .then(response => {
                    if (response.data.success) {
                        dispatch({
                            type: 'afterLoad',
                            value: {
                                data: response.data.results,
                                hash: hash(response.data.results),
                            }
                        });
                    } else {

                    }
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleGetPdfFile = (orderId: number) => {
        props.getAntibodyScreeningPdfFile(context.centerId, context.recipientId, orderId).then((response: any) => {
            downloadFile(response.data, `${orderId}-results.pdf`, 'application/pdf');
        });
    };

    const handleGetRawFile = (orderId: number) => {
        props.getAntibodyScreeningRawFile(context.centerId, context.recipientId, orderId).then((response: any) => {
            downloadFile(response.data, `${orderId}-results.csv`, 'text/csv');
        });
    };

    const handleViewDetails = (orderId: number) => {
        props.showLoading('Getting results, one moment please...').then(() => {
            props.getAntibodyScreeningResult(context.centerId, context.recipientId, orderId).then(response => {
                if (response.data.success) {
                    dispatch({
                        type: 'updateDetails',
                        value: {
                            show: true,
                            data: response.data.results
                        }
                    });
                }

                props.hideLoading();
            });
        });
    };

    const handleCloseDetails = () => {
        dispatch({
            type: 'updateDetails',
            value: {
                how: false,
                data: null
            }
        });
    };

    const handleProcessAntibodyScreeningOverride = (screeningId: number) => {
        //STEVE, IM HUNGRY!
        if (confirm('Are you sure you want to over-ride this DnDSA?')) {
            props.showLoading('Processing override, one moment please...').then(() => {
                props.processAntibodyScreeningOverride(context.centerId, context.recipientId, screeningId).then(response => {
                    if (response.data.success) {
                        toast.success('Successfuly over-ridden a DnDSA.');
                    } else {
                        toast.error('Failed to over-ride a DnDSA.');
                    }
                    props.hideLoading();
                });
            });
        }
    };

    const renderViewResults = (item: IAntibodyScreening) => {
        if (item.resultsPdfPath != '' && item.resultsRawPath != '') {
            return (
                <><Button bsSize='xs' onClick={() => handleGetPdfFile(item.id)}>Download PDF</Button> / <Button bsSize='xs' onClick={() => handleGetRawFile(item.id)}>Download CSV</Button></>
            );
        }

        if (item.resultsPdfPath != '' && item.resultsRawPath == '') {
            return (
                <><Button bsSize='xs' onClick={() => handleGetPdfFile(item.id)}>Download PDF</Button></>
            );
        }

        if (item.resultsPdfPath == '' && item.resultsRawPath != '') {
            return (
                <><Button bsSize='xs' onClick={() => handleGetRawFile(item.id)}>Download CSV</Button></>
            );
        }

        return <Label style={{ fontSize: '90%' }}>Results Pending</Label>;
    };

    if (localState.data == null || context.headerInfo == null)
        return null;

    const list = localState.data;

    return (
        <>
            <ProfileWrapper>
                <TableWrapper striped condensed>
                    <thead>
                        <tr>
                            <th>Order #</th>
                            <th>Order Date</th>
                            <th>Screening Type</th>
                            <th>Order Status</th>
                            <th>Results Date</th>
                            <th>View Results</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((item, index) => (
                            <tr key={'row' + index}>
                                <td>{item.id}</td>
                                <td>{item.sentDate}</td>
                                <td>{item.is30Day ? "Baseline" : "Annual"}</td>
                                <td>{item.resultsReceivedDate != '' ? "Completed" : <Label style={{ fontSize: '90%' }}>Results Pending</Label>}</td>
                                <td>{item.resultsReceivedDate != '' ? item.resultsReceivedDate : <Label style={{ fontSize: '90%' }}>Results Pending</Label>}</td>
                                <td>{renderViewResults(item)}</td>
                                <td>
                                    <Button bsStyle='primary' bsSize='xs' onClick={() => handleViewDetails(item.id)}>View Details</Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </TableWrapper>
            </ProfileWrapper>

            <AntibodyScreeningDetails
                show={localState.details.show}
                recipientAlias={context.headerInfo.alias}
                data={localState.details.data}
                processAntibodyScreeningOverride={handleProcessAntibodyScreeningOverride}
                onClose={handleCloseDetails}
            />
        </>
    );
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getAntibodyScreenings: (centerId, recipientId) => {
            return dispatch(RecipientActionCreators.getAntibodyScreenings(centerId, recipientId));
        },
        getAntibodyScreeningResult: (centerId, recipientId, screeningId) => {
            return dispatch(RecipientActionCreators.getAntibodyScreeningResult(centerId, recipientId, screeningId));
        },
        getAntibodyScreeningPdfFile: (centerId, recipientId, orderId) => {
            return dispatch(RecipientActionCreators.getAntibodyScreeningPdfFile(centerId, recipientId, orderId));
        },
        getAntibodyScreeningRawFile: (centerId, recipientId, orderId) => {
            return dispatch(RecipientActionCreators.getAntibodyScreeningRawFile(centerId, recipientId, orderId));
        },
        processAntibodyScreeningOverride: (centerId, recipientId, screeningResultsId) => {
            return dispatch(RecipientActionCreators.processAntibodyScreeningOverride(centerId, recipientId, screeningResultsId));
        },
    };
};

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(AntibodyScreening);